
import React, { useState, useEffect } from 'react';
import { X, Upload, Link as LinkIcon, FileText, Check, File } from 'lucide-react';

interface FilePickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (url: string, meta: { name: string; size: string; type: string }) => void;
  initialUrl?: string;
}

const FilePickerModal: React.FC<FilePickerModalProps> = ({ isOpen, onClose, onSave, initialUrl = '' }) => {
  const [activeTab, setActiveTab] = useState<'upload' | 'url'>('upload');
  const [urlInput, setUrlInput] = useState('');
  
  // File details
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewName, setPreviewName] = useState('');
  const [previewSize, setPreviewSize] = useState('');

  useEffect(() => {
    if (isOpen) {
      setUrlInput(initialUrl);
      setSelectedFile(null);
      setPreviewName('');
      setPreviewSize('');
    }
  }, [isOpen, initialUrl]);

  const formatSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setPreviewName(file.name);
      setPreviewSize(formatSize(file.size));
    }
  };

  const handleSave = () => {
    if (activeTab === 'url') {
       // Manual URL
       onSave(urlInput, { name: '', size: '', type: 'LINK' });
    } else {
       // File Upload
       if (selectedFile) {
         const reader = new FileReader();
         reader.onloadend = () => {
            const result = reader.result as string;
            // Detect extension
            const ext = selectedFile.name.split('.').pop()?.toUpperCase() || 'FILE';
            onSave(result, { 
                name: selectedFile.name, 
                size: previewSize,
                type: ext
            });
         };
         reader.readAsDataURL(selectedFile);
       } else {
         // No file selected, maybe just wanted to close?
         onClose();
         return;
       }
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-fade-in">
       <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
          {/* Header */}
          <div className="p-4 border-b flex justify-between items-center bg-gray-50">
             <h3 className="font-bold text-gray-800 flex items-center gap-2">
               <FileText size={20} className="text-blue-600"/>
               Wybierz Dokument
             </h3>
             <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-200">
               <X size={20} />
             </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b">
            <button 
              onClick={() => setActiveTab('upload')}
              className={`flex-1 py-3 text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${activeTab === 'upload' ? 'bg-white text-blue-600 border-b-2 border-blue-600' : 'bg-gray-50 text-gray-500 hover:bg-gray-100'}`}
            >
              <Upload size={16} /> Wybierz z dysku
            </button>
            <button 
              onClick={() => setActiveTab('url')}
              className={`flex-1 py-3 text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${activeTab === 'url' ? 'bg-white text-blue-600 border-b-2 border-blue-600' : 'bg-gray-50 text-gray-500 hover:bg-gray-100'}`}
            >
              <LinkIcon size={16} /> Zewnętrzny Link
            </button>
          </div>

          {/* Content */}
          <div className="p-6 flex-1 overflow-y-auto">
             {activeTab === 'upload' ? (
                <div className="space-y-4">
                  <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-blue-50 hover:border-blue-400 transition-all group relative overflow-hidden">
                    <div className="flex flex-col items-center justify-center pt-5 pb-6 z-10">
                        <div className="bg-white p-3 rounded-full shadow-sm mb-3 group-hover:scale-110 transition-transform">
                           <File className="w-8 h-8 text-gray-400 group-hover:text-blue-500" />
                        </div>
                        <p className="mb-1 text-sm text-gray-500 font-medium">Kliknij, aby wybrać plik</p>
                        <p className="text-xs text-gray-400">PDF, DOC, XLS, ZIP</p>
                    </div>
                    <input type="file" className="hidden" onChange={handleFileChange} />
                  </label>
                  
                  {selectedFile && (
                      <div className="bg-green-50 border border-green-200 rounded p-3 flex items-center gap-3">
                          <Check size={20} className="text-green-600"/>
                          <div>
                              <p className="text-sm font-bold text-green-800">{selectedFile.name}</p>
                              <p className="text-xs text-green-600">{previewSize}</p>
                          </div>
                      </div>
                  )}
                </div>
             ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1">Link do pliku (URL)</label>
                    <input 
                      autoFocus
                      className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                      placeholder="https://example.com/katalog.pdf"
                      value={urlInput}
                      onChange={(e) => setUrlInput(e.target.value)}
                    />
                  </div>
                </div>
             )}
          </div>

          {/* Footer */}
          <div className="p-4 border-t bg-gray-50 flex justify-end gap-3">
             <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:bg-gray-200 rounded-lg font-medium text-sm transition-colors">
               Anuluj
             </button>
             <button onClick={handleSave} className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold text-sm shadow-md transition-all flex items-center gap-2">
               <Check size={16} /> Wybierz i Zapisz
             </button>
          </div>
       </div>
    </div>
  );
};

export default FilePickerModal;
